import styled from '@emotion/styled'
import { mediaQueriesByDevice } from 'parameters/index'
import { useLocation } from 'services/FionaAPI/Lambdas/useLocation'
import { LoadingBoundary } from 'components/patterns/suspense/Suspense'
import { ResultList } from './ResultList'
import { SummaryItem } from './SummaryItem'
import { ResultListTitle } from './ResultListTitle'
import { OfferItemLoader } from './OfferItem'
import { createDataWrappedConfig } from 'components/patterns/suspense/componentWrappers'
import { useCalculatorLoansPreview } from 'services/FionaAPI/Calculator/hooks'
import { CALCULATOR_ESTIMATE_TOOLTIP_TEXT, InputState } from '../config'
import { DebtConsolidationCalcResult } from 'generated/api'
import { redirectToApply } from '../utils'
import { SectionTitle } from './SectionTitle'
import { Button } from 'ui/inputs/Button/LegacyButtonCompatibility'

interface Props {
	inputState: InputState
}

export interface SummaryConfig {
	topText: string
	savingsErrorString: string
	valueAddition: string
	calculateProposedValue: (offer: DebtConsolidationCalcResult) => number
	calculateCurrentValue: (offer: DebtConsolidationCalcResult) => number
}

const monthlySummaryConfig: SummaryConfig = {
	topText: 'New Monthly Payment',
	savingsErrorString: '$—/mo',
	valueAddition: '/mo',
	calculateProposedValue: (offer): number => {
		return offer.monthlyPaymentSummary?.bestValue as number
	},
	calculateCurrentValue: (offer): number => {
		return offer.monthlyPaymentSummary?.savings as number
	}
}

const interestSummaryConfig: SummaryConfig = {
	topText: 'Total Interest',
	savingsErrorString: '$— total',
	valueAddition: '',
	calculateProposedValue: (offer): number => {
		return offer.interestSummary?.bestValue as number
	},
	calculateCurrentValue: (offer): number => {
		return offer.interestSummary?.savings as number
	}
}

export const ResultsSection = ({ inputState }: Props): JSX.Element => {
	const [{ location }] = useLocation()

	const previewList = useCalculatorLoansPreview({
		zipcode: location?.postalCode || '10022',
		creditScore: 'excellent',
		currentDebt: inputState.debtAmount,
		currentMonthlyPayment: inputState.monthlyPayment,
		currentApr: inputState.interestRate / 100
	})

	const monthlySummary = createDataWrappedConfig(previewList, monthlySummaryConfig)
	const interestSummary = createDataWrappedConfig(previewList, interestSummaryConfig)

	return (
		<>
			<SectionTitle
				sectionNumber={2}
				title="Your Debt Consolidation Estimates"
				tooltipText={CALCULATOR_ESTIMATE_TOOLTIP_TEXT}
			/>
			<SummaryWrapper>
				<SummaryItem data={monthlySummary} />
				<SummaryItem data={interestSummary} />
			</SummaryWrapper>
			<ResultListTitle />

			<LoadingBoundary fallback={<OfferItemLoader />}>
				<ResultList inputState={inputState} data={previewList} />
			</LoadingBoundary>

			<Bottom>
				<BottomTitle>Think You’re Paying Too Much?</BottomTitle>
				<BottomSubTitle>Compare offers from top providers for debt consolidation.</BottomSubTitle>
				<Button onClick={() => redirectToApply(inputState.debtAmount)}>Get Started</Button>
			</Bottom>
		</>
	)
}

const SummaryWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	${mediaQueriesByDevice.mobile} {
		> div:nth-of-type(1) {
			width: 55%;
		}
	}
`

const Bottom = styled.div`
	display: flex;
	flex-direction: column;
	button {
		max-width: 280px;
		${mediaQueriesByDevice.mobile} {
			max-width: 100%;
		}
	}
`

const BottomTitle = styled.div`
	font-size: 18px;
	font-weight: bold;
	margin-bottom: 16px;
`

const BottomSubTitle = styled.div`
	font-size: 16px;
	margin-bottom: 24px;
`
